/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import React, { useContext, useRef } from 'react';
import type {
  WMSServiceInfo,
  LayerTree,
  LayerProps,
  Style,
  OGCWMSWMTSGetCapabilities,
  LayerFoundation,
} from '@opengeoweb/webmap';
import {
  WMLayer,
  webmapUtils,
  LayerType,
  privateWebMapUtils,
  wmDetermineDimensionMode,
} from '@opengeoweb/webmap';
import TimeContext from '../components/OpenLayers/context/TimeContext';

const {
  privateWmsGetCapabilities,
  privateGetWMSServiceInfo,
  privateWmsGetLayerTree,
  privateWmsFlattenLayerTree,
  privateWmtsGetCapabilities,
  QUERYWMS_GETCAPABILITIES,
} = privateWebMapUtils;

/**
 * Queries the WMS GetCapabilities. This is the base TanStack hook for the other hooks in this file.
 *
 * @param {string} serviceUrl
 * @returns {UseQueryResult<WMSGetCapabilities>}
 */
export const useQueryGetWMSGetCapabilities = (
  serviceUrl: string,
): UseQueryResult<OGCWMSWMTSGetCapabilities> => {
  const uriKey = new URLSearchParams(serviceUrl).toString();
  return useQuery({
    queryKey: [uriKey, QUERYWMS_GETCAPABILITIES],
    queryFn: async () => privateWmsGetCapabilities(serviceUrl),
  });
};

/**
 * Returns details about the WMS service
 *
 * @param {string} serviceUrl
 * @returns {(WMSServiceInfo | null)}
 */
export const useQueryGetWMSServiceInfo = (
  serviceUrl: string,
): WMSServiceInfo | null => {
  const [serviceInfo, setServiceInfo] = React.useState<WMSServiceInfo | null>(
    null,
  );
  const { data: getCapabilitiesJSON } =
    useQueryGetWMSGetCapabilities(serviceUrl);
  React.useEffect((): void => {
    if (!getCapabilitiesJSON) {
      setServiceInfo(null);
    } else {
      setServiceInfo(privateGetWMSServiceInfo(getCapabilitiesJSON, serviceUrl));
    }
  }, [serviceUrl, getCapabilitiesJSON]);
  return serviceInfo;
};

/**
 * Gets the layer tree in hierarchical order for a WMS service. Cached by TanStack. Uses the same store as in queryWMSGetCapabilities.
 *
 * @param {string} serviceUrl
 * @returns {(LayerTree | null)}
 */
export const useQueryGetWMSLayersTree = (
  serviceUrl: string,
): LayerTree | null => {
  const [layerTree, setLayerTree] = React.useState<LayerTree | null>(null);
  const { data: getCapabilitiesJSON } =
    useQueryGetWMSGetCapabilities(serviceUrl);
  React.useEffect((): void => {
    if (!getCapabilitiesJSON) {
      setLayerTree(null);
    } else {
      setLayerTree(privateWmsGetLayerTree(getCapabilitiesJSON));
    }
  }, [serviceUrl, getCapabilitiesJSON]);
  return layerTree;
};

/**
 * Gets the flat layer list (without hierarchy) for a WMS service. Cached by TanStack. Uses the same store as in queryWMSGetCapabilities.
 *
 * @param {string} serviceUrl
 * @returns {LayerProps[]}
 */
export const useQueryGetWMSLayers = (serviceUrl: string): LayerProps[] => {
  const [layers, setLayers] = React.useState<LayerProps[]>([]);
  const { data: getCapabilitiesJSON } =
    useQueryGetWMSGetCapabilities(serviceUrl);
  React.useEffect((): void => {
    if (!getCapabilitiesJSON) {
      setLayers([]);
    } else {
      const tree = privateWmsGetLayerTree(getCapabilitiesJSON);
      if (!tree) {
        setLayers([]);
      } else {
        setLayers(privateWmsFlattenLayerTree(tree));
      }
    }
  }, [serviceUrl, getCapabilitiesJSON]);
  return layers;
};

/**
 * Returns specific layer from the WMS service
 *
 * @param {string} serviceUrl
 * @param {string} name
 * @returns {(LayerProps | null)}
 */
export const useQueryGetWMSLayer = (
  serviceUrl: string,
  name: string,
): LayerProps | null => {
  const [layer, setLayer] = React.useState<LayerProps | null>(null);
  const layers = useQueryGetWMSLayers(serviceUrl);
  React.useEffect((): void => {
    setLayer(layers.find((layer) => layer.name === name) || null);
  }, [layers, name]);

  return layer;
};

/**
 * Returns a stylelist for given layer in a service
 *
 * @param {string} serviceUrl
 * @param {string} name
 * @returns {(Style[] | null)}
 */
export const useGetWMSLayerStyleList = (
  serviceUrl: string,
  name: string,
): Style[] | null => {
  return useQueryGetWMSLayer(serviceUrl, name)?.styles || null;
};

/**
 * Returns a WMLayer instance. If the layerType is LayerType.mapLayer or LayerType.wmtsMapLayer, the layer will detect the right type and will contain the parsed dimensions and styles.
 *
 * @param layerFoundation
 * @returns object with wmLayer and if set an errorMessage
 */
export const useGetWMLayerInstance = (
  layerFoundation?: LayerFoundation,
): { wmLayer: WMLayer | null; errorMessage: string | null } => {
  const id = layerFoundation?.id;
  const serviceUrl = layerFoundation?.service || '';
  const name = layerFoundation?.name || '';
  const layerType = layerFoundation?.layerType || LayerType.mapLayer;
  const [layer, setLayer] = React.useState<WMLayer | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const refId = useRef<string>(id || webmapUtils.generateLayerId());
  const layerId = refId.current!;

  const { refTimeDimensionModes } = useContext(TimeContext);

  // Check if the layer is already registered
  const tmpWMLayer = webmapUtils.getWMLayerById(layerId);
  const layerIdChanged = id && id !== tmpWMLayer?.id;
  const layerServiceChanged = tmpWMLayer?.service !== serviceUrl;
  if (layerServiceChanged || layerIdChanged) {
    // Make a new layer if the serviceUrl changes. (But not when the name changes)
    const refTimeDimensionMode = wmDetermineDimensionMode(
      { name, service: serviceUrl, id: layerId, layerType },
      refTimeDimensionModes,
    );
    const wmLayer = new WMLayer({
      name,
      service: serviceUrl,
      layerType,
      id: layerId,
      refTimeDimensionMode,
    });
    webmapUtils.registerWMLayer(wmLayer, layerId);
  }

  React.useEffect((): void => {
    // Set the name and parse the layer
    const wmLayer = webmapUtils.getWMLayerById(layerId);
    if (wmLayer.layerType !== layerType) {
      setErrorMessage('LayerType cannot change for the same layer id');
    }
    wmLayer
      .setName(name)
      .catch((e) => {
        setErrorMessage(e);
      })
      .finally(() => {
        setLayer(wmLayer);
      });
  }, [layerId, layerType, name, serviceUrl]);

  return { wmLayer: layer, errorMessage };
};

/**
 * Used for querying WMTS GetCapabilities documents
 * @param serviceUrl
 * @returns
 */
export const useQueryWMTSGetCapabilities = (
  serviceUrl: string,
): UseQueryResult<string> => {
  const uriKey = new URLSearchParams(serviceUrl).toString();
  return useQuery({
    queryKey: [uriKey, 'queryGetCapabilities'],
    queryFn: async () => privateWmtsGetCapabilities(serviceUrl, [], true),
  });
};
